import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { servicesData, serviceCategories } from '../data/servicesData';
import { useServiceSelection } from '../contexts/ServiceSelectionContext';

const ServiceBrowserModal = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { addService, isServiceSelected, removeService } = useServiceSelection();

  const extractDuration = (name, description) => {
    const durationMatch = name.match(/(\d+)\s*minute/) || 
                         name.match(/(\d+)\s*min/) ||
                         (description && description.match(/(\d+)\s*minute/));
    return durationMatch ? `${durationMatch[1]} min` : '30 min';
  };

  const handleToggleService = (serviceItem, parentService) => {
    const itemId = `${parentService.id}-${serviceItem.name}`;
    if (isServiceSelected(itemId)) {
      removeService(itemId);
    } else {
      const serviceToAdd = {
        id: itemId,
        name: `${parentService.title} - ${serviceItem.name}`,
        price: serviceItem.price,
        duration: extractDuration(serviceItem.name, serviceItem.description),
        category: parentService.category,
        description: serviceItem.description
      };
      addService(serviceToAdd);
    }
  };

  // Improved search functionality
  const filteredServices = useMemo(() => {
    if (!searchTerm.trim()) return servicesData;
    
    const searchLower = searchTerm.toLowerCase().trim();
    const searchWords = searchLower.split(' ').filter(word => word.length > 0);
    
    return servicesData.map(service => {
      // Check if service title matches
      const titleMatches = searchWords.every(word => 
        service.title.toLowerCase().includes(word)
      );
      
      // Check if category matches
      const categoryMatches = searchWords.every(word => 
        service.category.toLowerCase().includes(word)
      );
      
      // Filter individual services
      const filteredItems = service.services.filter(item => {
        const itemText = `${item.name} ${item.description || ''} ${item.price}`.toLowerCase();
        return searchWords.every(word => itemText.includes(word));
      });
      
      // If title or category matches, show all services
      if (titleMatches || categoryMatches) {
        return service;
      }
      
      // If individual services match, return service with filtered items
      if (filteredItems.length > 0) {
        return { ...service, services: filteredItems };
      }
      
      return null;
    }).filter(Boolean);
  }, [searchTerm]);

  // Group services by category for display
  const servicesByCategory = useMemo(() => {
    const grouped = {};
    serviceCategories.forEach(category => {
      grouped[category] = filteredServices.filter(service => service.category === category);
    });
    return grouped;
  }, [filteredServices]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
          <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900">
            Browse Services
          </DialogTitle>
        </DialogHeader>

        {/* Search Bar */}
        <div className="px-4 sm:px-6 pb-2 sm:pb-4">
          <Input
            type="text"
            placeholder="Search by service name, category, or price..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>

        {/* All Services Content - Continuous Scroll */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4 sm:pb-6">
          {filteredServices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No services found matching "{searchTerm}"
            </div>
          ) : (
            <div className="space-y-8">
              {serviceCategories.map((category) => {
                const categoryServices = servicesByCategory[category];
                if (!categoryServices || categoryServices.length === 0) return null;
                
                return (
                  <div key={category} className="">
                    {/* Category Header */}
                    <h2 className="text-lg sm:text-xl font-bold text-blue-600 mb-4 sticky top-0 bg-white z-10 py-2">
                      {category}
                    </h2>
                    
                    {/* Services in Category */}
                    <div className="space-y-4">
                      {categoryServices.map((service) => (
                        <div key={service.id} className="bg-gray-50 rounded-lg p-3 sm:p-5">
                          <h3 className="font-semibold text-base sm:text-lg mb-2 text-gray-900">{service.title}</h3>
                          {service.description && (
                            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">{service.description}</p>
                          )}
                          <div className="space-y-2 sm:space-y-3">
                            {service.services.map((item, index) => {
                              const itemId = `${service.id}-${item.name}`;
                              const isSelected = isServiceSelected(itemId);
                              
                              return (
                                <div
                                  key={index}
                                  className={`flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 p-3 sm:p-4 bg-white rounded-md border transition-colors ${
                                    isSelected ? 'border-green-400 bg-green-50' : 'border-gray-200'
                                  }`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm sm:text-base text-gray-900">{item.name}</p>
                                    {item.description && (
                                      <p className="text-xs sm:text-sm text-gray-600 mt-1">{item.description}</p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2 text-xs sm:text-sm">
                                      <span className="text-gray-500">
                                        {extractDuration(item.name, item.description)}
                                      </span>
                                      <span className="font-semibold text-blue-600">
                                        {item.price}
                                      </span>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => handleToggleService(item, service)}
                                    className={`w-full sm:w-auto sm:shrink-0 ${
                                      isSelected
                                        ? 'bg-red-500 hover:bg-red-600'
                                        : 'bg-blue-600 hover:bg-blue-700'
                                    } text-white`}
                                  >
                                    {isSelected ? 'Remove' : 'Add'}
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 pt-2 sm:pt-4 border-t bg-gray-50 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceBrowserModal;